import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Button, Flex, Input, InputGroup, InputProps } from '@chakra-ui/react';
import moment from 'moment/moment';
import { useCallback } from 'react';
import { translate } from '../../../i18n';

type WeekPickerProps = {
    selectedDate: Date;
    onChange: (date: Date) => void;
} & Omit<InputProps, 'value' | 'onChange' | 'type'>;

export function WeekPicker({ selectedDate, onChange, ...props }: WeekPickerProps) {
    const formatWeek = useCallback((date: Date) => {
        const weekStart = moment(date).startOf('week').format('DD/MM/YYYY');
        const weekEnd = moment(date).endOf('week').format('DD/MM/YYYY');
        return `${weekStart} - ${weekEnd}`;
    }, []);

    const handlePreviousWeek = () => {
        const newDate = moment(selectedDate).subtract(1, 'week').toDate();
        onChange(newDate);
    };

    const handleNextWeek = () => {
        const newDate = moment(selectedDate).add(1, 'week').toDate();
        onChange(newDate);
    };

    return (
        <Flex alignItems="center" gap={2}>
            <Button
                size="sm"
                variant="ghost"
                onClick={handlePreviousWeek}
                aria-label="Previous week"
            >
                <ChevronLeftIcon />
            </Button>

            <InputGroup size={props.size}>
                <Input
                    type="text"
                    value={formatWeek(selectedDate)}
                    readOnly
                    onClick={(e) => {
                        // When clicked, open the native week picker
                        const weekInput = document.createElement('input');
                        weekInput.type = 'week';
                        weekInput.value = moment(selectedDate).format('YYYY-[W]WW');
                        weekInput.style.display = 'none';
                        document.body.appendChild(weekInput);

                        weekInput.onchange = (event) => {
                            const weekStr = (event.target as HTMLInputElement).value; // Format: "2024-W01"
                            if (weekStr) {
                                const [year, week] = weekStr.split('-W');
                                const date = moment().year(parseInt(year)).week(parseInt(week)).startOf('week').toDate();
                                onChange(date);
                            }
                            document.body.removeChild(weekInput);
                        };

                        weekInput.click();
                    }}
                    cursor="pointer"
                    placeholder={translate('calendar.select_week')}
                    textAlign="center"
                    width="220px"
                    {...props}
                />
            </InputGroup>

            <Button
                size="sm"
                variant="ghost"
                onClick={handleNextWeek}
                aria-label="Next week"
            >
                <ChevronRightIcon />
            </Button>
        </Flex>
    );
}